import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { VaultKeTheme } from '../utils/theme';

interface FeatureProps {
  icon: string;
  title: string;
  description: string;
}

const Feature: React.FC<FeatureProps> = ({ icon, title, description }) => {
  const styles: { [key: string]: React.CSSProperties } = {
    feature: {
      ...VaultKeTheme.components.card,
      textAlign: 'center',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.surface} 0%, ${VaultKeTheme.colors.surfaceVariant} 100%)`,
      border: `1px solid ${VaultKeTheme.colors.border}`,
      backdropFilter: 'blur(10px)',
      boxShadow: `0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05)`,
    },
    iconContainer: {
      width: '80px',
      height: '80px',
      margin: '0 auto',
      marginBottom: VaultKeTheme.spacing.lg,
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.primary} 0%, ${VaultKeTheme.colors.primaryLight} 100%)`,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: `0 8px 16px rgba(0, 212, 170, 0.3)`,
    },
    icon: {
      fontSize: '2.5rem',
      color: VaultKeTheme.colors.background,
    },
    title: {
      ...VaultKeTheme.typography.h4,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.md,
      fontWeight: '700',
      background: `linear-gradient(135deg, ${VaultKeTheme.colors.primary} 0%, ${VaultKeTheme.colors.text} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
    },
    description: {
      ...VaultKeTheme.typography.body2,
      color: VaultKeTheme.colors.textSecondary,
      lineHeight: '1.7',
      fontSize: '0.95rem',
    },
    glow: {
      position: 'absolute',
      top: '-50%',
      left: '-50%',
      width: '200%',
      height: '200%',
      background: `radial-gradient(circle, rgba(0, 212, 170, 0.1) 0%, transparent 70%)`,
      opacity: 0,
      pointerEvents: 'none',
    },
  };

  return (
    <motion.div
      style={styles.feature}
      whileHover={{
        y: -10,
        boxShadow: `0 20px 40px rgba(0, 212, 170, 0.2), 0 0 0 1px rgba(0, 212, 170, 0.1)`,
      }}
      whileTap={{ scale: 0.98 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <motion.div
        style={styles.glow}
        whileHover={{ opacity: 1 }}
      ></motion.div>
      <motion.div
        style={styles.iconContainer}
        whileHover={{
          scale: 1.1,
          rotate: 5,
          boxShadow: `0 12px 24px rgba(0, 212, 170, 0.4)`,
        }}
        transition={{ type: "spring", stiffness: 400, damping: 10 }}
      >
        <motion.div
          style={styles.icon}
          animate={{ rotate: [0, 10, -10, 0] }}
          transition={{
            duration: 2,
            repeat: Infinity,
            repeatDelay: Math.random() * 3 + 2,
          }}
        >
          {icon}
        </motion.div>
      </motion.div>
      <motion.h3
        style={styles.title}
        whileHover={{ scale: 1.05 }}
      >
        {title}
      </motion.h3>
      <motion.p
        style={styles.description}
        whileHover={{ color: VaultKeTheme.colors.text }}
      >
        {description}
      </motion.p>
    </motion.div>
  );
};

const Features: React.FC = () => {
  const [columns, setColumns] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setColumns(1);
      } else if (window.innerWidth < 1024) {
        setColumns(2);
      } else {
        setColumns(3);
      }
    };
    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles: { [key: string]: React.CSSProperties } = {
    features: {
      padding: `${VaultKeTheme.spacing.xxl} ${VaultKeTheme.spacing.xl}`,
      backgroundColor: VaultKeTheme.colors.surface,
    },
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: VaultKeTheme.spacing.xxl,
    },
    title: {
      ...VaultKeTheme.typography.h2,
      color: VaultKeTheme.colors.text,
      marginBottom: VaultKeTheme.spacing.lg,
    },
    subtitle: {
      ...VaultKeTheme.typography.body1,
      color: VaultKeTheme.colors.textSecondary,
      maxWidth: '600px',
      margin: '0 auto',
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, 1fr)`,
      gap: VaultKeTheme.spacing.xl,
    },
  };

  const features = [
    {
      icon: '🛡️',
      title: 'Military-Grade Security',
      description: 'Fort Knox-level protection with end-to-end encryption, biometric authentication, and zero-trust architecture safeguarding every transaction.',
    },
    {
      icon: '🚀',
      title: 'AI-Powered Intelligence',
      description: 'Machine learning algorithms predict trends, optimize contributions, and provide personalized financial insights for maximum growth.',
    },
    {
      icon: '⚡',
      title: 'Lightning-Fast Transactions',
      description: 'Instant M-Pesa integrations, real-time balance updates, and seamless cross-border transfers at the speed of thought.',
    },
    {
      icon: '🎯',
      title: 'Precision Analytics',
      description: 'Crystal-clear dashboards with predictive modeling, ROI tracking, and automated reporting that turns data into decisions.',
    },
    {
      icon: '🌐',
      title: 'Unified Ecosystem',
      description: 'Single platform connecting mobile apps, web dashboards, and API integrations for a frictionless financial experience.',
    },
    {
      icon: '💎',
      title: 'Premium Experience',
      description: 'Elegant interface design meets powerful functionality, delivering enterprise-grade features with consumer-grade simplicity.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  return (
    <motion.section
      style={styles.features}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={containerVariants}
    >
      <div style={styles.container}>
        <motion.div style={styles.header} variants={cardVariants}>
          <motion.h2
            style={styles.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Powerful Features for Modern Chamas
          </motion.h2>
          <motion.p
            style={styles.subtitle}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
          >
            Everything you need to manage, grow, and secure your community savings group
            with cutting-edge technology and intuitive design.
          </motion.p>
        </motion.div>
        <motion.div
          style={styles.grid}
          variants={containerVariants}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={cardVariants}>
              <Feature
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Features;